import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@chakra-ui/react";
import { mutationErrorHandler } from "am-tax-fe-core";
import { TOAST_DURATION } from "../../utils/constants";
import { queryKeys } from "../queryKeys";
import {
    CreateReportTransaction,
    UpdateReportTransaction,
    createReportTransactionAsync,
    getReportTransactionAsync,
    updateReportTransactionAsync,
    deleteReportTransactionAsync,
} from "./reportTransactionApi";

export function useGetReportTransactions(reportId: string) {
    return useQuery({
        ...queryKeys.reportTransactions.byReportId(reportId),
        queryFn: async () => {
            try {
                return await getReportTransactionAsync(reportId);
            } catch (error) {
                // If no report transactions exist (404), return null instead of throwing
                if (error && typeof error === "object" && "status" in error && error.status === 404) {
                    return null;
                }
                throw error; // Re-throw other errors
            }
        },
        enabled: !!reportId,
    });
}

export function useGetReportTransaction(reportId: string) {
    return useQuery({
        ...queryKeys.reportTransactions.detail(reportId),
        queryFn: async () => {
            try {
                return await getReportTransactionAsync(reportId);
            } catch (error) {
                // If no report transactions exist (404), return null instead of throwing
                if (error && typeof error === "object" && "status" in error && error.status === 404) {
                    return null;
                }
                throw error; // Re-throw other errors
            }
        },
        enabled: !!reportId,
    });
}

export function useCreateReportTransaction() {
    const queryClient = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: async (args: CreateReportTransaction) => {
            return await createReportTransactionAsync(args);
        },
        onSuccess: async data => {
            await queryClient.invalidateQueries(queryKeys.reportTransactions.byReportId(data.reportId));
        },
        ...mutationErrorHandler(toast, "Unable to create report transaction"),
    });
}

export function useUpdateReportTransaction() {
    const queryClient = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: async (args: { reportId: string; clientId: string; data: UpdateReportTransaction }) => {
            return await updateReportTransactionAsync(args.reportId, args.data);
        },
        onSuccess: async (data, variables) => {
            await queryClient.invalidateQueries(queryKeys.reports.byId(data.reportId));
            await queryClient.invalidateQueries(queryKeys.reportTransactions.detail(data.reportId));
            await queryClient.invalidateQueries(queryKeys.reports.byClientId(variables.clientId));
            await queryClient.invalidateQueries(queryKeys.reportTransactions.byReportId(data.reportId));
            toast({
                title: "Updated",
                description: "Report transaction updated successfully.",
                status: "success",
                duration: TOAST_DURATION,
                isClosable: true,
            });
        },
        ...mutationErrorHandler(toast, "Unable to update report transaction"),
    });
}

export function useDeleteReportTransaction() {
    const queryClient = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: async (reportId: string) => {
            return await deleteReportTransactionAsync(reportId);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries(queryKeys.reportTransactions.all);
            toast({
                title: "Deleted",
                description: "Report transaction deleted successfully.",
                status: "success",
                duration: TOAST_DURATION,
                isClosable: true,
            });
        },
        ...mutationErrorHandler(toast, "Unable to delete report transaction"),
    });
}
